import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { Avatar } from '../components/Avatar';
import type { ContactSummary } from '../types';

/**
 * Contacts page — saved recipients that aren't (yet) Clients. Soft-deleted
 * contacts disappear from this list but their messages stay attached for
 * history. "Promote to client" copies the contact into Clients and repoints
 * its messages.
 */
export function Contacts({ onPromoted }: { onPromoted?: (clientId: string) => void } = {}) {
  const { t } = useTranslation(['contacts', 'common']);

  const [items, setItems] = useState<ContactSummary[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContactSummary | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ContactSummary | null>(null);
  const [confirmPromote, setConfirmPromote] = useState<ContactSummary | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch<{ items: ContactSummary[] }>(
        `/api/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      );
      setItems(r.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const handle = setTimeout(reload, 200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function handleDelete(id: string) {
    try {
      await apiFetch(`/api/contacts/${id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown');
    }
  }

  async function handlePromote(id: string) {
    try {
      const r = await apiFetch<{ clientId: string }>(`/api/contacts/${id}/promote`, {
        method: 'POST',
      });
      setConfirmPromote(null);
      if (onPromoted) onPromoted(r.clientId);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown');
    }
  }

  return (
    <>
      <div className="contacts-toolbar">
        <div className="field">
          <input
            type="text"
            placeholder={t('contacts:list.search_placeholder', {
              defaultValue: 'Search contacts…',
            })}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button className="btn-primary" type="button" onClick={() => setShowCreate(true)}>
          {t('contacts:new.button', { defaultValue: '+ New contact' })}
        </button>
      </div>

      {error && (
        <div className="empty">{t('common:errors.with_message', { message: error })}</div>
      )}

      {loading && items.length === 0 && (
        <div className="empty">{t('common:actions.loading')}</div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="empty">{t('contacts:list.empty', { defaultValue: 'No contacts yet.' })}</div>
      )}

      {items.length > 0 && (
        <div className="full-panel">
          {items.map((c) => (
            <div key={c.id} className="case-row">
              <Avatar name={c.name} size="sm" />
              <div className="case-info">
                <div className="case-name">{c.name}</div>
                <div className="case-sub">
                  {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div className="contact-row-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditing(c)}>
                  {t('common:actions.edit')}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setConfirmPromote(c)}
                >
                  {t('contacts:promote.button', { defaultValue: 'Promote' })}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setConfirmDelete(c)}
                >
                  {t('common:actions.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <ContactForm
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}

      {editing && (
        <ContactForm
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t('contacts:delete.title', { defaultValue: 'Delete contact?' })}
          message={t('contacts:delete.confirm', {
            defaultValue: 'Past messages stay attached. The contact disappears from this list.',
          })}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete.id)}
        />
      )}

      {confirmPromote && (
        <ConfirmDialog
          title={t('contacts:promote.title', { defaultValue: 'Promote to client?' })}
          message={t('contacts:promote.confirm', {
            defaultValue:
              'Creates a new Client record from this contact and moves their message history.',
          })}
          onCancel={() => setConfirmPromote(null)}
          onConfirm={() => handlePromote(confirmPromote.id)}
        />
      )}
    </>
  );
}

function ContactForm({
  contact,
  onClose,
  onSaved,
}: {
  contact?: ContactSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['contacts', 'common']);
  const isEdit = !!contact;
  const [name, setName] = useState(contact?.name ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
      };
      if (isEdit) {
        await apiFetch(`/api/contacts/${contact!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch('/api/contacts', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown';
      const key = `contacts:errors.${code}`;
      const translated = t(key);
      setError(translated !== key ? translated : t('common:errors.with_message', { message: code }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div className="form-preview-name">
              {isEdit
                ? t('contacts:edit.title', { defaultValue: 'Edit contact' })
                : t('contacts:new.title', { defaultValue: 'New contact' })}
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="field">
                <label>
                  {t('contacts:fields.name', { defaultValue: 'Name' })}{' '}
                  <span className="req">*</span>
                </label>
                <input value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="field">
                <label>{t('contacts:fields.email', { defaultValue: 'Email' })}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="field">
                <label>{t('contacts:fields.phone', { defaultValue: 'Phone' })}</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section" style={{ color: 'var(--red)' }}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
              {busy ? t('common:actions.saving') : t('common:actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDialog({
  title,
  message,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div className="form-preview-name">{title}</div>
        </div>
        <div className="form-section">
          <p style={{ color: 'var(--text2)', fontSize: 13 }}>{message}</p>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            {t('actions.cancel')}
          </button>
          <button type="button" className="btn-primary" onClick={onConfirm}>
            {t('actions.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
